// src/antdviews/AccountDetailView.tsx
// 账户详情页面 - Ant Design版本

import axios from 'axios'
import React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Layout, message } from 'antd'
import 'react-toastify/dist/ReactToastify.css'

import Header from './components/Header'
import Sidebar from './components/Sidebar'
import { FilePath, URL, UserMgmtApi } from '../services/Api'
import { getSourceName, LOG_SOURCE, NEW_RECORD_SYSTEM, RECORD_LOCATION, REPORT_TYPE, ROLE } from '../services/Macro'
import { checker } from '../utils/Checker'
import AccountDetailContent from './components/AccountDetailContent'
import { strUtil } from '../utils/StrUtil'
import { timeUtil } from '../utils/TimeUtil'
import ConfirmModal from '../components/ConfirmModal'
import WarnModal from '../components/WarnModal'
import { Global } from '../services/Global'
import { encrypUtil } from '../utils/EncrypUtil'
import { Page } from '../services/Page'

interface AccountDetailViewState {
    showWarn: boolean
    showConfirm: boolean
    warnTitle: string
    warnInfo: string
    confirmOp: () => void
    detail: {
        isActive: boolean
        accountName: string
        accountEmail: string
        onlinePortrait: string | null
        users: { list: any[] }
        logs: { list: any[] }
        records: { list: any[] }
        phoneInfo: {
            phoneMode: string
            phoneManufacturer: string
            phoneVersion: string
            phoneSDK: string
            hasPhoneMode: boolean
            hasPhoneManufacturer: boolean
            hasPhoneVersion: boolean
            hasPhoneSDK: boolean
        }
    }
    loginName: string
    recordSize: number
    recordPage: number
    logSize: number
    logPage: number
}

const warnModalStyles = {
    content: {
        width: '480px',
        top: '40%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: '-20px',
    },
}

class AccountDetailView extends React.Component<WithTranslation, AccountDetailViewState> {
    constructor(props: WithTranslation) {
        super(props)
        this.state = {
            showWarn: false,
            showConfirm: false,
            warnTitle: '本地错误',
            warnInfo: '参数错误',
            confirmOp: () => { console.log('do nothing!') },
            detail: {
                isActive: false,
                accountName: '',
                accountEmail: '',
                onlinePortrait: null,
                users: { list: [] },
                logs: { list: [] },
                records: { list: [] },
                phoneInfo: {
                    phoneMode: '',
                    phoneManufacturer: '',
                    phoneVersion: '',
                    phoneSDK: '',
                    hasPhoneMode: false,
                    hasPhoneManufacturer: false,
                    hasPhoneVersion: false,
                    hasPhoneSDK: false,
                }
            },
            loginName: '',
            recordSize: 10,
            recordPage: 1,
            logSize: 10,
            logPage: 1,
        }
    }

    componentDidMount() {
        const DEBUG = true
        const TAG = `AccountDetailView.componentDidMount() - `
        const { t } = this.props
        DEBUG && console.log(TAG, `进入账号详情页面时的登录校验`)
        const callChecker = async () => {
            console.log(`数据校验`)
            let token = localStorage.getItem('token') || null
            let lang = localStorage.getItem('lang') || 'zh_CN'
            let isLogin = await checker.checkLogin(ROLE.SUPER, Page.accountDetail)
            if (isLogin) {
                const searchParams = new URLSearchParams(window.location.search)
                const userId = searchParams.get('userId')
                const uuid = searchParams.get('uuid')
                const requestData = {
                    role: ROLE.SUPER,
                    token: token,
                    accountId: userId,
                    uuid: uuid,
                    lang: lang.split('_')[0]
                }
                DEBUG && console.log(TAG, `requestData:\n`, requestData)
                let params = await encrypUtil.encodeRequestRsa(requestData)
                DEBUG && console.log(TAG, `params:\n`, params)
                // 请求页面数据
                const api = NEW_RECORD_SYSTEM ? UserMgmtApi.SUPER_GET_ACCOUNT_DETAIL_2 : UserMgmtApi.SUPER_GET_ACCOUNT_DETAIL
                const response = await axios.post(api, params)
                DEBUG && console.log(TAG, `获取账号详情:\n`, response.data.data)
                // 设置页面数据
                let users = response.data.data.users
                for (let i = 0; i < users.list.length; i++) {
                    let user = users.list[i];
                    user.created = strUtil.formatTime(user.created)
                    user.genderStr = user.gender === 0 ? t('male') : t('female')
                    // 处理名字连接
                    if (user.middleName) user.name += ` ${user.middleName}`
                    if (user.lastName) user.name += ` ${user.lastName}`
                    // 处理生日连接
                    user.birthday = ''
                    if (user.yearBirth) {
                        user.birthday += `${user.yearBirth}`
                        if (user.monthBirth) {
                            user.birthday += `-${strUtil.fillPrev(`${user.monthBirth}`, 2, '0')}`
                            if (user.dayBirth) {
                                user.birthday += `-${strUtil.fillPrev(`${user.dayBirth}`, 2, '0')}`
                                user.age = timeUtil.getAgeFromBirth(user.birthday)
                            }
                        }
                    }
                }
                // 处理记录数据
                let records = response.data.data.records
                this.handleRecords(records)
                // 处理日志数据
                let logs = response.data.data.logs
                this.handleLogs(logs)
                let accountName = response.data.data.accountName
                let accountEmail = response.data.data.accountEmail
                let isActive = response.data.data.isActive
                let onlinePortrait = response.data.data.onlinePortrait
                let phoneInfo = response.data.data.phoneInfo
                if (!phoneInfo) {
                    phoneInfo = {
                        phoneMode: t('unknown'),
                        phoneManufacturer: t('unknown'),
                        phoneVersion: t('unknown'),
                        phoneSDK: t('unknown'),
                        hasPhoneMode: false,
                        hasPhoneManufacturer: false,
                        hasPhoneVersion: false,
                        hasPhoneSDK: false,
                    }
                }
                else {
                    phoneInfo.hasPhoneMode = phoneInfo.phoneMode ? true : false
                    !phoneInfo.hasPhoneMode && (phoneInfo.phoneMode = t('unknown'))
                    phoneInfo.hasPhoneManufacturer = phoneInfo.phoneManufacturer ? true : false
                    !phoneInfo.hasPhoneManufacturer && (phoneInfo.phoneManufacturer = t('unknown'))
                    phoneInfo.hasPhoneVersion = phoneInfo.phoneVersion ? true : false
                    !phoneInfo.hasPhoneVersion && (phoneInfo.phoneVersion = t('unknown'))
                    phoneInfo.hasPhoneSDK = phoneInfo.phoneSDK ? true : false
                    !phoneInfo.hasPhoneSDK && (phoneInfo.phoneSDK = t('unknown'))
                }
                this.setState({
                    loginName: 'Admin',
                    detail: {
                        isActive: isActive,
                        onlinePortrait: onlinePortrait,
                        users: users,
                        logs: logs,
                        records: records,
                        accountName: accountName,
                        accountEmail: accountEmail,
                        phoneInfo: phoneInfo
                    }
                })
            }
        }
        callChecker()
    }

    handleRecords = (records: any) => {
        const { t } = this.props
        let recordPages = []
        for (let i = 0; i < records.totalPages; i++) {
            recordPages.push(i + 1);
        }
        console.log(`recordPages:`, recordPages)
        records.pages = recordPages
        for (let i = 0; i < records.list.length; i++) {
            let record = records.list[i]
            record.create = strUtil.formatTime(record.create)
            record.rate = record.meta.rate || 512
            record.size = strUtil.toM(record.size)
            record.timeLength = '00:00:00'
            record.nickname = ''
            let firmVersion = '()'
            let gn = null
            let exportFileName = null
            if (record.meta) {
                if (record.meta.exportFileName && record.meta.exportFileName != '') exportFileName = record.meta.exportFileName
                if (record.meta.timeLength) record.timeLength = strUtil.formatDuration(record.meta.timeLength)
                // 拼接 name + middleName + lastName
                if (record.meta.name) record.nickname = record.meta.name
                if (record.meta.middleName) record.nickname += ` ${record.meta.middleName}`
                if (record.meta.lastName) record.nickname += ` ${record.meta.lastName}`

                if (record.meta.startTime) record.startTime = strUtil.formatTime(record.meta.startTime * 1000)
                if (record.meta.gn) gn = `${record.meta.gn}`
                if (record.meta.firmwareVersion)
                    firmVersion = `${gn == null ? '' : '_' + gn}(${record.meta.firmwareVersion.replace("GHealth_DEMO_", "").replace("_DRV_LIB", "")})`
                if (record.meta.field05) record.description = record.meta.field05
            }
            if (exportFileName) {
                record.fileName = exportFileName
            }
            else {
                record.fileName = `HMApp_Record_${record.rate}_${strUtil.formatTime(record.startTime, 'YYYYMMDD-HHmm-ss')}${firmVersion}`
            }

            record.reports = []
            if ((record.reportTypes & 1 << (REPORT_TYPE.SLEEP - 1)) != 0) {
                record.reports.push({ name: 'sleep', type: REPORT_TYPE.SLEEP })
            }

            // 指明来源
            record.sourceStr = getSourceName(record.source)

            // 拼接链接地址
            if (record.location === RECORD_LOCATION.DATA_FILES) {
                record.href = `${URL}/download.do?apikey=1F192D3A136F4DAF9B6A4E3E07893E2D&url=${record.url}`
            }
            else if (record.location === RECORD_LOCATION.UPLOADS_RECORDS) {
                const shorUrl = record.url.slice(12, 12 + 32)
                record.href = `${URL}/admin/records/${shorUrl}`
            }
            else if (record.location === RECORD_LOCATION.FILES_RECORDS) {
                const shorUrl = record.url.slice(12, 12 + 32)
                record.href = `${FilePath.RECORDS}${shorUrl}`
            }
            console.log(`record.href:\n`, record.href)
        }
    }

    handleLogs = (logs: any) => {
        let logPages = []
        for (let i = 0; i < logs.totalPages; i++) {
            logPages.push(i + 1);
        }
        logs.pages = logPages
        for (let i = 0; i < logs.list.length; i++) {
            let log = logs.list[i];
            let date = `${log.date.split("-").join("")}`
            const sourceStr = log.source == LOG_SOURCE.ANDROID ? '' : `_${log.source}`
            log.filePath = `${log.uuid}${sourceStr}_${date}.log`
            log.fileName = `HMApp_app_${date}.log`
            log.size = strUtil.toM(log.size)
            log.updateTime = strUtil.formatTime(log.updateTime)
            log.rowspan = 1

            if (i > 0) {
                if (log.date == logs.list[i - 1].date) {
                    logs.list[i - 1].rowspan = 2
                    log.hideDate = true
                }
            }
        }
    }

    changeRecordList = async (size: number, page: number) => {
        const DEBUG = true
        const TAG = `AccountDetailView.changeRecordList() - `
        this.setState({ recordSize: size, recordPage: page }, async () => {
            // 仅请求账户的记录列表
            const searchParams = new URLSearchParams(window.location.search)
            const userId = searchParams.get('userId')
            const uuid = searchParams.get('uuid')
            let token = localStorage.getItem('token') || null
            let lang = localStorage.getItem('lang') || 'zh_CN'
            let requestData = {
                role: ROLE.SUPER,
                token: token,
                accountId: userId,
                uuid: uuid,
                lang: lang.split('_')[0],
                page: this.state.recordPage,
                size: this.state.recordSize,
            }
            DEBUG && console.log(TAG, `requestData:\n`, requestData)
            let params = await encrypUtil.encodeRequestRsa(requestData)
            DEBUG && console.log(TAG, `params:\n`, params)
            // 请求页面数据
            const response = await axios.post(NEW_RECORD_SYSTEM ? UserMgmtApi.SUPER_GET_RECORD_LIST_2 : UserMgmtApi.SUPER_GET_RECORD_LIST, params)
            DEBUG && console.log(TAG, `获取记录列表:\n`, response.data.data)
            let records = response.data.data
            this.handleRecords(response.data.data)
            this.setState({
                loginName: this.state.loginName,
                detail: {
                    isActive: this.state.detail.isActive,
                    onlinePortrait: this.state.detail.onlinePortrait,
                    users: this.state.detail.users,
                    logs: this.state.detail.logs,
                    records: records,
                    accountName: this.state.detail.accountName,
                    accountEmail: this.state.detail.accountEmail,
                    phoneInfo: this.state.detail.phoneInfo
                }
            })
        })
    }

    changeLogList = async (size: number, page: number) => {
        const DEBUG = true
        const TAG = `AccountMgmtView.changeLogList() - `
        this.setState({ logSize: size, logPage: page }, async () => {
            // 仅请求账户的日志列表
            const searchParams = new URLSearchParams(window.location.search)
            const userId = searchParams.get('userId')
            const uuid = searchParams.get('uuid')
            let token = localStorage.getItem('token') || null
            let lang = localStorage.getItem('lang') || 'zh_CN'
            // 请求页面数据
            let requestData = {
                role: ROLE.SUPER,
                token: token,
                accountId: userId,
                uuid: uuid,
                lang: lang.split('_')[0],
                page: this.state.logPage,
                size: this.state.logSize,
            }
            DEBUG && console.log(TAG, `requestData:\n`, requestData)
            let params = await encrypUtil.encodeRequestRsa(requestData)
            DEBUG && console.log(TAG, `params:\n`, params)
            const response = await axios.post(UserMgmtApi.SUPER_GET_LOG_LIST, params)
            DEBUG && console.log(TAG, `获取日志列表:\n`, response.data.data)
            let logs = response.data.data
            this.handleLogs(response.data.data)
            this.setState({
                loginName: this.state.loginName,
                detail: {
                    isActive: this.state.detail.isActive,
                    onlinePortrait: this.state.detail.onlinePortrait,
                    users: this.state.detail.users,
                    logs: logs,
                    records: this.state.detail.records,
                    accountName: this.state.detail.accountName,
                    accountEmail: this.state.detail.accountEmail,
                    phoneInfo: this.state.detail.phoneInfo
                }
            })
        })
    }

    changeLang = () => {
        const { t } = this.props

        // 列表中的性别字段切换语言
        let users: any = this.state.detail.users
        for (let i = 0; i < users.list.length; i++) {
            let user = users.list[i];
            user.genderStr = user.gender === 0 ? t('male') : t('female')
        }
        this.state.detail.users = users

        // 用户概览中如果设备信息未知则需要切换语言
        let phoneInfo: any = this.state.detail.phoneInfo
        if (!phoneInfo.hasPhoneMode) phoneInfo.phoneMode = t('unknown')
        if (!phoneInfo.hasPhoneManufacturer) phoneInfo.phoneManufacturer = t('unknown')
        if (!phoneInfo.hasPhoneVersion) phoneInfo.phoneVersion = t('unknown')
        if (!phoneInfo.hasPhoneSDK) phoneInfo.phoneSDK = t('unknown')

        this.state.detail.phoneInfo = phoneInfo
    }

    openConfirm = (title: string, info: string, op: Function) => {
        this.setState({
            warnTitle: title,
            warnInfo: info,
            showConfirm: true,
            confirmOp: () => {
                op()
                this.closeConfirm()
            }
        })
    }

    closeConfirm = () => {
        this.setState({ showConfirm: false })
    }

    handleError = (title: string, info: string) => {
        this.setState({
            warnTitle: title,
            warnInfo: info,
            showWarn: true
        })
    }

    notify = (msg: string) => {
        message.success(msg)
    }

    closeWarn = () => {
        this.setState({ showWarn: false })
    }

    render() {
        const { t, i18n } = this.props;
        const { loginName } = this.state;
        return (
            <section className="body">
                <WarnModal
                    isMobile={Global.isMobile}
                    isOpen={this.state.showWarn}
                    onRequestClose={this.closeWarn}
                    title={t('error')}
                    subtitle={this.state.warnTitle}
                    confirm={t('confirm')}
                    customStyles={warnModalStyles}>
                    {this.state.warnInfo}
                </WarnModal>
                <ConfirmModal
                    isMobile={Global.isMobile}
                    isOpen={this.state.showConfirm}
                    onRequestConfirm={this.state.confirmOp}
                    onRequestCancel={this.closeConfirm}
                    title={t('warning')}
                    subtitle={this.state.warnTitle}
                    confirm={t('confirm')}
                    cancel={t('cancel')}
                    customStyles={warnModalStyles}>
                    {this.state.warnInfo}
                </ConfirmModal>
                {/* <Header trans={t} i18n={i18n} loginName={this.state.loginName} changeLang={this.changeLang}></Header>
                <div className="inner-wrapper">
                    <Sidebar selectMenu={Page.accountMgmt} trans={t}></Sidebar>
                    <AccountDetailContent
                        detail={this.state.detail}
                        trans={t}
                        handleConfirm={this.openConfirm}
                        handleError={this.handleError}
                        notify={this.notify}
                        changeRecordList={this.changeRecordList}
                        changeLogList={this.changeLogList}
                    />
                </div> */}
                <Layout style={{ minHeight: '100vh' }}>
                    <Header
                        trans={t}
                        i18n={i18n}
                        loginName={loginName}
                        changeLang={this.changeLang}
                    />
                    <Layout>
                        <Sidebar selectMenu={Page.accountMgmt} trans={t} />
                        <AccountDetailContent
                            detail={this.state.detail}
                            trans={t}
                            handleConfirm={this.openConfirm}
                            handleError={this.handleError}
                            notify={this.notify}
                            changeRecordList={this.changeRecordList}
                            changeLogList={this.changeLogList}
                        />
                    </Layout>
                </Layout>
            </section>
        )
    }
}

export const AccountDetailPage = withTranslation()(AccountDetailView)